import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Image from 'elements/image/image';
import Paragraph from 'elements/typography/paragraph';
import { PhoneCarouselContentfulModel } from 'src/api/models/PhoneCarousel'
import Heading from 'elements/typography/heading'
import { ButtonContentfulModel } from 'src/api/models/Button'
import LinkBranded from 'elements/links/Link.branded'
import LinkSimple from 'elements/links/Link.simple'
import {
  ImageContainer,
  ImageShadow,
  RightArrow,
  LeftArrow,
  CoursesImagesSliderContainer,
  CoursesImageSlider,
  CoursesCarouselContainer,
  CoursesCarouselHeadingContainer,
  CoursesCarouselButtonContainer,
  ImageCaptionContainer,
  PhoneBlockImageTop,
  CoursesCarouselShapeBackground,
  CoursesCarouselSection
} from './courses.carousel.styles';

export interface CoursesImagesProps {
  title: string;
  images: PhoneCarouselContentfulModel[ 'contentfulchildren' ]
  actionButtons?: ButtonContentfulModel[]
}

const CoursesCarousel = ({ title, images, actionButtons }: CoursesImagesProps): JSX.Element => {
  const [ currentSlide, setCurrentSlide ] = useState(0)
  const data: any = useStaticQuery(graphql`{
  file(relativePath: {eq: "phone-mockup.png"}) {
    childImageSharp {
      gatsbyImageData(width: 346, layout: CONSTRAINED, placeholder: BLURRED)
    }
  }
}
`);

  const sliderSettings = {
    infinite: true,
    centerMode: true,
    draggable: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 1000,
    responsive: [
      {
        breakpoint: 950, // layout.subLg
        settings: {
          slidesToShow: 1
        }
      },
      {
        breakpoint: 700, // layout.sm
        settings: {
          slidesToShow: 1
        }
      }
    ],
    nextArrow: <RightArrow />,
    prevArrow: <LeftArrow />,
    beforeChange: (_oldIndex: number, newIndex: number) => setCurrentSlide(newIndex)
  }

  return (
    <CoursesCarouselSection>
      <CoursesCarouselShapeBackground />
      <CoursesCarouselContainer>
        <CoursesCarouselHeadingContainer>
          <Heading
            align="center"
            size="2.5em"
            sizeLg="2em"
            sizeSubLg="1.5em"
            text={title}
          />
        </CoursesCarouselHeadingContainer>
        <CoursesImagesSliderContainer>
          <CoursesImageSlider {...sliderSettings}>
            {
              images.map((image, index) => (
                <React.Fragment key={index}>
                  <ImageContainer visible={currentSlide === index}>
                    <Image childImageSharp={data.file.childImageSharp.gatsbyImageData} alt="phone-background" />
                    <ImageShadow>
                      <Image childImageSharp={image.image.gatsbyImageData} alt={image.description} />
                      <PhoneBlockImageTop />
                    </ImageShadow>
                  </ImageContainer>
                  <ImageCaptionContainer visible={currentSlide === index}>
                    <Heading
                      text={image.title}
                      size="1.5em"
                      weight="normal"
                      align="center"
                    />
                    <Paragraph
                      text={image.description}
                      size="1em"
                      weight="light"
                      align="center"
                    />
                  </ImageCaptionContainer>
                </React.Fragment>
              ))
            }
          </CoursesImageSlider>
        </CoursesImagesSliderContainer>
        {
          actionButtons?.length ?
            <CoursesCarouselButtonContainer>
              {
                actionButtons.map((button) => {
                  if (button.variant.includes('primary')) {
                    return <LinkBranded url={button.url} key={button.id} text={button.text} caption={button.helpText} justify="center" captionJustify="center" weight="bold" />
                  } else {
                    return <LinkSimple url={button.url} color="main" key={button.id} text={button.text} iconPosition="right" justify="center" icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.66 15.8" height="11px"><path d="M18.84,8.26A24.57,24.57,0,0,1,15,5.14,11.73,11.73,0,0,1,13.45,1.6l-2.9.8a15,15,0,0,0,2.17,4.65,11.65,11.65,0,0,0,.93,1H0v3H13.65a11.65,11.65,0,0,0-.93.95,15,15,0,0,0-2.17,4.65l2.9.8A12,12,0,0,1,15,13.86a24.73,24.73,0,0,1,3.82-3.12L20.66,9.5Z" transform="translate(0 -1.6)"></path></svg>} />
                  }
                })
              }
            </CoursesCarouselButtonContainer>
            : null
        }
      </CoursesCarouselContainer>
    </CoursesCarouselSection>
  )
}

export default CoursesCarousel;
